<!--
  Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
  This information is confidential and proprietary to GM Global Technology
  Operations LLC and may not be used, modified, copied or distributed.
  -->

<br/>
<h1 class="page-header">KTS Admin Page</h1>
<br/>
@if (permission()) {
  <div>
    @if (loadingMomentumDataSignal() || !ktsTimingTemplateRowMapSignal()?.size) {
      <p-progressBar mode="indeterminate" [style]="{ height: '6px' }"></p-progressBar>
    }
    <table style="width:100%">
      <tr>
        <td><h3 style="font-size: x-large; font-weight: bold">{{getPrefix()}} Timing Chart</h3></td>
        <p-confirmDialog key="saveTimingsConfirmation"></p-confirmDialog>
        <p-confirmDialog key="studyModeConfirmation"></p-confirmDialog>
        <td><div class="buttons" style="float: right;">
          <p-select [options]="types" [(ngModel)]="type" (ngModelChange)="onTemplateTypeChange()"
            [disabled]="isTemplateTypeSelectorDisabled()"
            placeholder="Template Type" class="mr-2">
          </p-select>
          @if (type === 'ECS') {
            <p-select [options]="ecsRpoOptions()" [ngModel]="selectedEcsRpo()" (ngModelChange)="onEcsRpoChange($event)"
              [disabled]="loadingMomentumDataSignal() || !ktsTimingTemplateRowMapSignal()?.size" [filter]="true" [showClear]="true"
              placeholder="Select GPS RPO" class="mr-2">
            </p-select>
            <p-select [options]="ecsModelYearOptions()" [ngModel]="selectedEcsModelYear()" (ngModelChange)="onEcsModelYearChange($event)"
              [disabled]="loadingMomentumDataSignal() || !ktsTimingTemplateRowMapSignal()?.size" [filter]="true" [showClear]="true"
              placeholder="Select Model Year" class="mr-2">
            </p-select>
            <p-select [options]="ecsProgramOptions()" [ngModel]="selectedEcsProgram()" (ngModelChange)="onEcsProgramChange($event)"
              [disabled]="loadingMomentumDataSignal() || !ktsTimingTemplateRowMapSignal()?.size" [filter]="true" [showClear]="true"
              optionLabel="projectName" placeholder="Select Program" class="mr-2">
            </p-select>
          } @else {
            <p-select [options]="modelYears" [(ngModel)]="selectedModelYear"
              [disabled]="loadingMomentumDataSignal() || !ktsTimingTemplateRowMapSignal()?.size"
              placeholder="Select Model Year" filterBy="year" optionLabel="year" class="mr-2">
            </p-select>
            <p-select [options]="momentumProgramMap.get(selectedModelYear?.year)" [(ngModel)]="selectedMomentumProgram" [showClear]="true" class="mr-2"
              placeholder="Select Program" optionLabel="programName" [filter]="true" filterBy="programName"
              [disabled]="!selectedModelYear || !momentumProgramMap.get(selectedModelYear.year)?.length || loadingMomentumDataSignal() || !ktsTimingTemplateRowMapSignal()?.size">
            </p-select>
          }
          <p-select [options]="versionDropdownOptions()" [(ngModel)]="selectedVersion" [showClear]="true" class="mr-2"
            placeholder="Override Version?" optionLabel="label" optionValue="value"
            [disabled]="isVersionOverrideDisabled()">
          </p-select>
          <p-button label="Get Program Timing" icon="pi pi-calendar-plus" [loading]="loadingMomentumDataSignal()" class="gm-btn-primary mr-2"
            [disabled]="!canLoadProgramTiming()" (click)="getMomentumProgramByMomentumId();">
          </p-button>
          <p-button label="Turn Study Mode {{studyMode? 'Off' : 'On'}}" icon="pi pi-wrench" [loading]="loadingMomentumDataSignal()" class="gm-btn-primary mr-2"
            (click)="toggleStudyMode();" [disabled]="selectedMomentumProgramList.length < 1">
          </p-button>
          @if (!studyMode) {
            <p-button label="Save Program Timing" icon="pi pi-save" [loading]="saving" class="gm-btn-primary mr-2"
              [disabled]="programNamesToSave() === '' || studyMode" (click)="saveProgramTiming(true);">
            </p-button>
          }
          <p-button label="Clear Programs" [disabled]="!selectedMomentumProgramList?.length"
          [severity]="!selectedMomentumProgramList?.length ? 'secondary' : 'warn'" (click)="clearPrograms()" class="mr-2" icon="pi pi-replay"></p-button>
          <p-splitbutton icon="pi pi-download" [disabled]="!selectedMomentumProgramList?.length"
            [severity]="!selectedMomentumProgramList?.length ? 'secondary' : null"
            label="Export Report" class="mr-2"
          [model]="visibleDownloadButtonOptions"></p-splitbutton>
          <p-splitbutton icon="pi pi-filter" [disabled]="!selectedMomentumProgramList?.length"
            [severity]="!selectedMomentumProgramList?.length ? 'secondary' : null"
            label="{{filterDuration > 0 ? 'Within ' + filterDuration + ' Days' : '30/60/90'}}" class="mr-2"
          [model]="durationButtonOptions"></p-splitbutton>
          @if (type === 'PROD') {
            <p-button label="ICDe Component Timing" class="gm-btn-primary mr-2"
              [loading]="isIcdePrimaryLoading()" [disabled]="isIcdePrimaryDisabled()"
              (click)="openPrimaryTimingModal()">
            </p-button>
          }
        </div></td>
      </tr>
    </table>
    @if (ktsTimingTemplateRowMapSignal().get(type)) {
      <p-tabs [value]="activeTabIndexSignal()" (valueChange)="setActiveTabIndex(+$event)">
        <p-tablist>
          @for (entry of fullCols.get(type) | keyvalue; track entry; let i = $index) {
            <p-tab [value]="i" (click)="handleTabClick(entry.key)">
              <span>
                <i class="pi pi-calendar"></i>
                Version {{KtsTimingTemplateRow.getMajorVersionFromFullVersion(entry.key)}} {{getStatusOfVersionForLabel(entry.key, type)}}
                ({{ktsTimingTemplateRowMapSignal().get(type).get(entry.key)[0].programData.size}})
              </span>
            </p-tab>
          }
          @if (selectedMomentumProgramList?.length) {
            <p-tab [value]="fullCols.get(type)?.size">
              <span>
                <i class="pi pi-calendar-minus"></i>
                Gantt Chart
              </span>
            </p-tab>
          }
        </p-tablist>
        <p-tabpanels>
          @for (entry of fullCols.get(type) | keyvalue; track entry; let i = $index) {
            <p-tabpanel [value]="i">
              <p>
                <ag-grid-angular style="width: 100%; height: 80vh;" class="ag-theme-balham key-task-scheduler"
                  [paginationPageSize]="paginationPageSize" [rowHeight]="30"
                  (columnVisible)="onColumnVisible($event, entry.key)"
                  [paginationPageSizeSelector]="paginationPageSizeSelector"
                  [columnDefs]="fullCols.get(type).get(entry.key)"
                  [rowData]="ktsTimingTemplateRowMapSignal().get(type).get(entry.key)" [pagination]="true"
                  [autoSizeStrategy]="autoSizeStrategy" [rowClassRules]="rowClassRules"
                  [defaultColDef]="defaultColDef"
                  (cellEditingStopped)="onCellEditingStopped($event)"
                (gridReady)="onGridReady($event, entry.key)"></ag-grid-angular>
              </p>
            </p-tabpanel>
          }
          @if (selectedMomentumProgramList?.length) {
            <p-tabpanel [value]="fullCols.get(type)?.size">
              <div style="width: 90%; height: 600px; overflow: hidden; position: relative;">
                @if (activeTabIndexSignal() === fullCols.get(type)?.size) {
                  <app-gantt-chart app-gantt-chart
                  [ganttChart]="ganttChart"></app-gantt-chart>
                }
              </div>
            </p-tabpanel>
          }
        </p-tabpanels>
      </p-tabs>
    }
    <div style="display: block; width: 1px; height: 1px; overflow: hidden;">
      <div style="display: block; width: 1000px; height: 1000px; overflow: hidden;">
        <div #imageExportElement>
          @if (exportingGantt) {
            <app-gantt-chart #exportGantt app-gantt-chart [ganttChart]="ganttChart" trueSize="true"/>
          }
        </div>
      </div>
    </div>
    @if (type === 'PROD' && programRuns && programRuns.get('PROD') && programRuns.get('PROD').length) {
      <p-dialog header="Select Program Timings for ICDe Dates Report" [(visible)]="showTimingSelectionModal"
        [modal]="true" [closable]="true" [style]="{height: '100%', width: '70vw'}" resizable="true">
        <div style="width: 100%; height: 70vh;">
          <ag-grid-angular class="ag-theme-balham" style="width: 100%; height: 100%;" [rowData]="programRuns.get('PROD')"
            [columnDefs]="columnDefs"
            [defaultColDef]="defaultColDef" [paginationPageSize]="500"
            [pagination]="true" [paginationPageSizeSelector]="paginationPageSizeSelector"
            (gridReady)="onModalGridReady($event)"
            (cellValueChanged)="onPrimaryProgramCellValueChanged()">
          </ag-grid-angular>
          <p>Primary Count: {{primaryProgramCount}}</p>
        </div>
        <div class="buttons" style="float: right;">
          <p-button gmSecondary label="Reset" class="mr-2" icon="pi pi-undo"
          (onClick)="resetPrimaryPrograms()"></p-button>
          <p-button gmPrimary label="Save" class="mr-2" icon="pi pi-save"
          (onClick)="savePrimaryTimingPrograms()"></p-button>
        </div>
      </p-dialog>
    }
  </div>
}
